import React from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { Location } from 'history';

import {
  hasRequiredRoleSelector,
  isAdminAccessDeniedSelector,
  isAuthenticatedSelector,
  isTeammateSelector,
} from 'redux/selectors/authenticationSelectors';
import { UserRole } from 'enums';
import ProfileSetupRoute from './ProfileSetupRoute';
import { useAppSelector } from 'hooks/useAppSelector';

type Props = {
  [propName: string]: any;
  roles: UserRole[];
  teammateRoles?: UserRole[];
};

const ONBOARDING_PATHNAMES = ['/show-around', '/tour'];
// A wrapper for <Route> that redirects to the login
// screen if you're not yet authenticated.
export default function PrivateRoute(props: Props) {
  const { roles, teammateRoles = [], ...rest } = props;
  const location = useLocation();
  const isAdminAccessDenied = useAppSelector(isAdminAccessDeniedSelector);
  const isAuthenticated = useAppSelector(isAuthenticatedSelector);
  const hasRequiredRole = useAppSelector(hasRequiredRoleSelector(roles));
  const isTeammateRole = useAppSelector(isTeammateSelector);
  const hasRequiredTeammateRoleSelector = useAppSelector(
    hasRequiredRoleSelector(teammateRoles),
  );

  const hasSubUserAccess =
    isTeammateRole && teammateRoles.length > 0
      ? hasRequiredTeammateRoleSelector
      : true;

  const renderContentForAuthenticatedUser = (location: Location) => {
    const isOnboardingPage = ONBOARDING_PATHNAMES.includes(location.pathname);

    if (!hasSubUserAccess || !hasRequiredRole) {
      return (
        <Navigate
          to={isOnboardingPage ? '/' : '/access-denied'}
          state={{ from: location }}
        />
      );
    }
    return (
      <ProfileSetupRoute {...rest}>
        <Outlet />
      </ProfileSetupRoute>
    );
  };

  const renderContent = (location: Location) => {
    if (isAuthenticated) {
      return isAdminAccessDenied ? (
        <Navigate to="/two-fa-activation" state={{ from: location }} />
      ) : (
        renderContentForAuthenticatedUser(location)
      );
    }

    return <Navigate to="login" state={{ from: location }} />;
  };

  return renderContent(location);
}
